import React, { FC, useCallback, useState } from 'react';
import { AggregatedProvider } from '@workboard/action-item';

import { AICopyDialog } from './dialog';
import { useCopyAI } from './useCopyAI';
import { useTracking } from '../utils/useTracking';
import * as events from './tracking';
import { FormData } from './useCopyAIForm';
import { getGlobalConfig } from '../global/ConfigProvider';
import { onMessage } from '../global/onMessage';

interface Props {
  id: string;
  initialData?: Partial<FormData>;
  onClose: () => void;
}

const WrappedDialog = ({ id, initialData, onClose }: Props) => {
  const trackEvent = useTracking(events.dialogDisplayed, { aiID: id });
  const [isClosed, setClosed] = useState(false);
  const closeAfterNotification = useCallback(() => {
    setClosed(true);

    // TODO remove timeout after we don't need to wait for the toast messages
    // Currently onClose callback causes unmounting dialog from DOM which
    // prevents toast messages from rendering
    setTimeout(onClose, 5000);
  }, [onClose]);
  const closeAfterCancel = useCallback(() => {
    trackEvent(events.dialogCanceled, { aiID: id });
    onClose();
  }, [onClose, trackEvent, id]);
  const { isProcessing, types, submit } = useCopyAI(id, closeAfterNotification);
  return isClosed ? null : (
    <AICopyDialog
      id={id}
      onClose={closeAfterCancel}
      onSubmit={submit}
      initialData={initialData}
      isProcessing={isProcessing}
      types={types}
    />
  );
};

export const AICopyStandaloneDialog: FC<Props> = ({
  id,
  initialData,
  onClose,
}: Props) => (
  <AggregatedProvider id={id} config={getGlobalConfig()} onMessage={onMessage}>
    <WrappedDialog id={id} onClose={onClose} initialData={initialData} />
  </AggregatedProvider>
);
